import { FC, useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Breadcrumbs, Button, Card, CardContent, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormControlLabel, Grid, IconButton, InputAdornment, Link, makeStyles, Switch, Tooltip, Typography, useTheme } from "@material-ui/core";
import ChevronRightIcon from "../../icons/ChevronRight";
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import CustomSkeleton from "src/components/skeleton/Skeleton";
import { FieldArray, Formik } from "formik";
import { Widget } from "./Types/WidgetType";
import InputField from "src/components/ui-components/InputField";
import SelectField from "src/components/ui-components/SelectField";
import { DefaultWidgetTagMatchAllOptions } from "../applicationSettings/AppSettingsPage";
import APIConfigurationsCard from "./APIConfigurationsCard";
import { GetAppMenusAndSubmenusWithUserGroup, getCustomWidgetFieldTypeMaster, getFieldDataTypeFormat, getFieldTypes, getResponseParams, getWebMethodsList, getWidgetByKey, getWidgetTypes, getCategoryList, insertUpdateWidgetDetails } from "./ConnectionService";
import AlertMessage from "src/components/ui-components/AlertMessage";
import UserGroupPageMappingType, { CheckedMenuList, FlatMenu, Page, PageGroup } from "./Types/MenuAndSubmenuType";
import WidgetFilterCard from "./WidgetFilterCard";
import { ExpandMore, Info } from "@material-ui/icons";
import MenuTreeForWidgets from "./MenuTreeWithUserGroups";
import Delete from "@material-ui/icons/Delete";

interface ParamsType {
  widgetKey: string;
};

const defaultForm: Widget = {
  WidgetKey: null,
  Name: null,
  Category: null,
  Type: null,
  Tag: null,
  WidgetTagMatchAll: null,
  RowNumber: null,
  IsInitialLoadWidget: false,
  API: [{
    WebMethodKey: null,
    Fields: [{
      FieldName: null,
      DisplayName: null,
      Type: null,
      Format: null,
      APIResponse: null,
      MapField: null,
      Icon: null,
      SortOrder: 1
    }],
    WidgetSort: [],
    WidgetFieldGrouping: [],
    WidgetFieldFilter: []
  }],
  Filter: {
    FilterName: null,
    DisplayName: null,
    GroupByValue: null,
    Values: []
  },
  PageMapping: [],
  WidgetCustomFormat: [],
  WidgetGroupFormat: []
};


const useStyles = makeStyles((theme) => ({
  tooltip: {
    fontSize: '0.8rem'
  }
}));

const WidgetCreateEditNew: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const formRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { widgetKey } = (location.state as ParamsType) || { widgetKey: null };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isDropDownFetching, setIsDropdownFetching] = useState<boolean>(false);
  const [isTreeLoading, setIsTreeLoading] = useState<boolean>(false);
  const [areDeleteChanges, setAreDeleteChanges] = useState<boolean>(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [menuList, setMenuList] = useState<UserGroupPageMappingType[]>([]);
  const [pageGroupList, setPageGroupList] = useState<FlatMenu[]>([]);
  const [checkedMenuList, setCheckedMenuList] = useState<CheckedMenuList[]>([]);
  const [TypeOptions, setTypeOptions] = useState([]);
  const [FieldTypeOptions, setFieldTypeOptions] = useState([]);
  const [FieldFormatOptions, setFieldFormatOptions] = useState([]);
  const [customFormatOptions, setCustomFormatOptions] = useState([]);
  const [webMethodsData, setWebMethodsData] = useState([]);
  const [APIResponseParams, setAPIResponseParams] = useState([]);
  const [shouldCheckInitialLoad, setShouldCheckInitialLoad] = useState<boolean>(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState<string | null>(null);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [categoryOptions, setcategoryOptions] = useState([]);
  const WidgetTagMatchAllOptions = [
    {
      Label: "Select",
      Value: null
    },
    ...DefaultWidgetTagMatchAllOptions
  ];

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: widgetKey && true },
      });
    }
  };

  const handleCancelButtonClick = (event) => {
    event.preventDefault();
    if (areDeleteChanges) {
      setCancelDialogOpen(true);
    } else {
      navigate(-1);
    }
  };

  const handleInitialLoadWidget = (event) => {
    const isChecked = event.target.checked;
    formRef.current.setFieldValue('IsInitialLoadWidget', isChecked);
    if (isChecked) {
      setCheckedMenuList([]);
    }
    setShouldCheckInitialLoad(isChecked);
  };

  const handleSave = () => {
    if (formRef && formRef.current && !formRef.current.isSubmitting) {
      formRef.current.submitForm();
    }
  };

  // const validateFilterGroupBy = () => {
  //   if (formRef && formRef.current && formRef.current.values && formRef.current.values.Filter.GroupByValue && formRef.current.values.Filter.GroupByValue !== '') {
  //     const selectedWebMethodKeys = formRef?.current?.values?.API?.map(x => x.WebMethodKey);
  //     const groupByValueOptions = APIResponseParams.filter(x => selectedWebMethodKeys.includes(x.webMethodKey)).map(x => x.parameterTagDisplayName);
  //     if (!groupByValueOptions.includes(formRef.current.values.Filter.GroupByValue)) {
  //       formRef?.current?.setFieldError('Filter.GroupByValue', 'Must be one of the API Responses');
  //       return false;
  //     }
  //     return true;
  //   } else {
  //     return true;
  //   }
  // };

  const validateMappedPageGroups = () => {
    if (checkedMenuList && checkedMenuList.length > 0) {
      const filtered = checkedMenuList.flatMap(x =>
        pageGroupList.filter(y =>
          y.PageGroupKey === x.PageGroupKey &&
          y.PageKey === x.PageKey &&
          y.UserGroupKey === x.UserGroupKey
        )
      );
      return !filtered.some(x => x.IsEnabled !== true);
    } else {
      return true;
    }
  };

  const handleTypeChange = (event) => {
    const selectedValue = event?.widgetTypeMasterKey || null;
    const selectedOption = TypeOptions.find(option => option.widgetTypeMasterKey === selectedValue);
    setSelectedWidgetType(selectedOption?.type || null);

    if (selectedOption?.type?.toLowerCase() !== 'keynumbers') {
      const newApiList = formRef?.current?.values?.API.map(apiEntry => ({
        ...apiEntry,
        Fields: apiEntry.Fields.map(field => ({
          ...field,
          Icon: null
        }))
      }));
      formRef.current.setFieldValue('API', newApiList);
      // setSelectedWidgetType(null);
    }

    formRef.current.setFieldValue('Type', selectedValue);
  };


  const fetchWidgetByKey = async (widgetKey) => {
    setIsFetching(true);
    await getWidgetByKey(widgetKey)
      .then((res) => {
        // Mapping API details
        const widgetAPIDetails = res.api.map(api => ({
          WebMethodKey: api.webMethodKey,
          Fields: api?.fields?.map(field => ({
            FieldName: field.fieldName,
            DisplayName: field.displayName,
            Type: field.type,
            Format: field.format,
            SortOrder: field.sortOrder,
            APIResponse: field.apiResponse,
            MapField: field.mapField,
            Icon: field.icon
          })) || [],
          WidgetSort: api?.widgetSort?.map(sort => ({
            SortType: sort.sortType,
            SortField: sort.sortField
          })) || [],
          WidgetFieldGrouping: api?.widgetFieldGrouping?.map(grouping => ({
            GroupByField: grouping.groupByField,
            GroupByAggField: grouping.groupByAggField
          })) || [],
          WidgetFieldFilter: api?.widgetFieldFilter?.map(filter => ({
            FilterField: filter.filterField,
            FilterValue: filter.filterValue
          })) || []
        }));

        // Mapping filter details
        const widgetFilterDetails = {
          FilterName: res.filter.filterName || null,
          DisplayName: res.filter.displayName || null,
          GroupByValue: res.filter.groupByValue || null,
          Values: res.filter?.widgetFilterValues?.map(value => ({
            Tag: value.tag || null,
            Value: value.value || null,
            IsDefault: value.isDefault || false,
            SortOrder: value.sortOrder || 0,
            IsEnabled: value.isEnabled || false,
          })) || []
        };

        // Mapping widget custom format and group format
        const widgetCustomFormat = res?.widgetCustomFormat?.map(format => ({
          DisplayName: format.displayName,
          FieldType: format.fieldType,
          Format: format.format
        })) || [];

        const widgetGroupFormat = res?.widgetGroupFormat?.map(format => ({
          FieldType: format.fieldType,
          Format: format.format
        })) || [];

        // Final widget data
        const widgetData = {
          WidgetKey: res.widgetKey,
          Name: res.name,
          Category: res.category,
          Type: res.type,
          Tag: res.tag,
          IsInitialLoadWidget: res.isInitialLoadWidget,
          WidgetTagMatchAll: res.widgetTagMatchAll,
          RowNumber: res.rowNumber,
          API: widgetAPIDetails,
          Filter: widgetFilterDetails,
          WidgetCustomFormat: widgetCustomFormat,
          WidgetGroupFormat: widgetGroupFormat
        };

        setShouldCheckInitialLoad(res.isInitialLoadWidget);
        setSelectedFieldType(widgetGroupFormat?.[0]?.FieldType ?? null);
        resetFormData(widgetData);

        // Mapping page mappings
        const pageMappings = res?.pageMappings?.map(x => ({
          PageGroupKey: x.pageGroupKey,
          PageKey: x.pageKey,
          UserGroupKey: x.userGroupKey
        }));

        setCheckedMenuList(pageMappings || []);

        // Setting selected widget type
        const selectedOption = TypeOptions.find(option => option.widgetTypeMasterKey === res.type);
        setSelectedWidgetType(selectedOption ? selectedOption.type : null);

        setIsFetching(false);
      })
      .catch((err) => {
        setIsFetching(false);
        AlertMessage({ type: "error", msg: err.title });
      });
  };

  const fetchDDLData = async () => {
    setIsDropdownFetching(true);

    const categoryList = await getCategoryList();
    setcategoryOptions(categoryList || []);
    const widgetTypes = await getWidgetTypes();
    setTypeOptions(widgetTypes || []);

    setIsTreeLoading(true);
    const menus = await GetAppMenusAndSubmenusWithUserGroup();
    setMenuList(menus);
    const pageGroups = menus.flatMap((menu: UserGroupPageMappingType) =>
      menu.pageGroups.flatMap((pageGroup: PageGroup) => {
        if (pageGroup.pages && pageGroup.pages.length > 0) {
          return pageGroup.pages.map((page: Page) => ({
            UserGroupKey: menu.userGroupKey,
            PageGroupKey: pageGroup.pageGroupKey,
            PageKey: page.pageKey,
            IsEnabled: page.isEnabled,
          }));
        } else {
          return [{
            UserGroupKey: menu.userGroupKey,
            PageGroupKey: pageGroup.pageGroupKey,
            PageKey: null,
            IsEnabled: pageGroup.isEnabled,
          }];
        }
      })
    );
    setPageGroupList(pageGroups);
    setIsTreeLoading(false);

    const webMethods = await getWebMethodsList();
    setWebMethodsData(webMethods || []);

    const fieldTypes = await getFieldTypes();
    setFieldTypeOptions(fieldTypes || []);

    const fieldFormats = await getFieldDataTypeFormat();
    setFieldFormatOptions(fieldFormats || []);

    const customFormats = await getCustomWidgetFieldTypeMaster();
    setCustomFormatOptions(customFormats || []);

    const apiResponseParams = await getResponseParams();
    setAPIResponseParams(apiResponseParams || []);
    setIsDropdownFetching(false);
  };

  useEffect(() => {
    if (formRef?.current?.values?.Type && TypeOptions) {
      const selectedOption = TypeOptions.find(option => option.widgetTypeMasterKey === formRef.current.values.Type);
      setSelectedWidgetType(selectedOption ? selectedOption.type : null);
    }
  }, [TypeOptions, formRef?.current?.values?.Type]);

  useEffect(() => {
    fetchDDLData();
    if (widgetKey) {
      fetchWidgetByKey(widgetKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              container
              alignItems="center"
              sx={{ my: 2 }}
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    {widgetKey ? 'Edit' : 'Create'} Widget
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Button
                      component={RouterLink}
                      to=""
                      sx={{ background: "linear-gradient(to bottom, #939ca3, #939ca3)", minWidth: 100 }}
                      variant="contained"
                      onClick={handleCancelButtonClick}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100 }}
                      disabled={isLoading}
                      onClick={handleSave}
                      variant="contained"
                    >
                      Save
                      {isLoading && (<CircularProgress color="primary" thickness={5} size={20} sx={{ ml: 1 }} />)}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          {(isFetching || isDropDownFetching) && <CustomSkeleton skeletonType="FormEdit" />}
          <Box sx={{ display: (isFetching || isDropDownFetching) ? "none" : "" }}>
            <Formik
              enableReinitialize
              innerRef={formRef}
              initialValues={defaultForm}
              validationSchema={Yup.object({
                Name: Yup.string()
                  .nullable()
                  .required("Name is required")
                  .max(100, "Name must be at most 100 characters")
                  .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Name"),
                Category: Yup.string()
                  .nullable()
                  .required('Category is required'),
                Type: Yup.string()
                  .nullable(),
                Tag: Yup.string()
                  .nullable()
                  .max(100, "Tag must be at most 100 characters")
                  .matches(/^(?!^0+$)[a-zA-Z0-9, ]+$/, "Please enter a valid Tag"),
                WidgetTagMatchAll: Yup.string()
                  .nullable(),
                RowNumber: Yup.number()
                  .typeError('Invalid Row Order')
                  .nullable(),
                WidgetCustomFormat: Yup.array().of(
                  Yup.object().shape({
                    DisplayName: Yup.string()
                      .nullable()
                      .required('Field is required'),
                    FieldType: Yup.string()
                      .nullable()
                      .required('Type is is required'),
                    Format: Yup.string()
                      .nullable()
                      .required('Format is required')
                  })
                ),
                API: Yup.array().of(
                  Yup.object().shape({
                    WebMethodKey: Yup.string()
                      .nullable()
                      .required('API is required'),
                    Fields: Yup.array().of(
                      Yup.object().shape({
                        FieldName: Yup.string()
                          .nullable()
                          .required('Field Name is required')
                          .max(100, "Field Name must be at most 100 characters")
                          .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Field Name"),
                        DisplayName: Yup.string()
                          .nullable()
                          .max(100, "Display Name must be at most 100 characters")
                          .required("Display Name is required")
                          .matches(/^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/, "Please enter a valid Display Name"),
                        Type: Yup.string()
                          .required("Type is required")
                          .nullable(),
                        Format: Yup.string()
                          .nullable(),
                        APIResponse: Yup.string()
                          .nullable()
                          .required('API Response is required'),
                        MapField: Yup.string()
                          .nullable()
                          .max(50, "Map Field must be at most 50 characters")
                          .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Map Field"),
                        SortOrder: Yup.string()
                          .matches(/^\d+$/, 'Invalid Sort Order')
                          .required('Sort Order is required')
                          .test('is-number', 'Invalid Sort Order', value => !isNaN(Number(value)))
                          .test('is-min', 'Invalid Sort Order', value => Number(value) >= 1)
                          .test({
                            name: "no-exceed-count",
                            test: function (value) {
                              const match = this.path.match(/API\[(\d+)\]/);
                              const apiFields = this.options.context.API[match[1]].Fields;
                              if (value > apiFields.length || apiFields.filter(x => Number(x.SortOrder) === Number(value)).length > 1) {
                                return false;
                              }
                              return true;
                            },
                            message: "Invalid Sort Order"
                          })
                          .nullable(),
                        Icon: Yup.string()
                          .nullable()
                          .when([], {
                            is: () => selectedWidgetType !== null && selectedWidgetType === 'keynumbers',
                            then: Yup.string().required('Icon is required').nullable(),
                          }),
                      })
                    ),
                    WidgetSort: Yup.array().of(
                      Yup.object().shape({
                        SortField: Yup.string()
                          .nullable()
                          .required('Sort Field is required'),
                        SortType: Yup.string()
                          .nullable()
                          .required('Sort Type is required')
                      })
                    ),
                    WidgetFieldFilter: Yup.array().of(
                      Yup.object().shape({
                        FilterField: Yup.string()
                          .nullable()
                          .required('Filter Field is required'),
                        FilterValue: Yup.string()
                          .nullable()
                          .required('Filter Value is required')
                      })
                    ),
                    WidgetFieldGrouping: Yup.array().of(
                      Yup.object().shape({
                        GroupByField: Yup.string()
                          .nullable(),
                        GroupByAggField: Yup.string()
                          .nullable()
                          .when('GroupByField', {
                            is: (GroupByField) => !!GroupByField,
                            then: Yup.string().required('Aggregate is required when Field is selected').nullable(),
                            otherwise: Yup.string().nullable(),
                          }),
                      })
                    ),
                  })
                )
                  .min(1, 'API Details are required'),
                Filter: Yup.object().shape({
                  FilterName: Yup.string()
                    .nullable()
                    .max(100, "Filter Name must be at most 100 characters")
                    .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Filter Name")
                    .test({
                      name: "requiredWithOtherFields",
                      test: function (value) {
                        const { DisplayName, GroupByValue } = this.parent;
                        if (
                          (DisplayName || GroupByValue) &&
                          (DisplayName?.trim() !== '' || GroupByValue?.trim() !== '') && (!value || value.trim() === '')
                        ) {
                          return false;
                        }
                        return true;
                      },
                      message: "Filter Name is required"
                    }),
                  DisplayName: Yup.string()
                    .nullable()
                    .max(100, "Display Name must be at most 100 characters")
                    .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Display Name"),
                  GroupByValue: Yup.string()
                    .nullable()
                    .max(100, "Group By Value must be at most 100 characters"),
                  Values: Yup.array().of((
                    Yup.object().shape({
                      Tag: Yup.string()
                        .nullable()
                        .max(100, "Filter Value Tag must be at most 100 characters")
                        .required('Filter Value Tag is required')
                        .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Tag"),
                      Value: Yup.string()
                        .nullable()
                        .max(500, "Filter Value must be at most 500 characters")
                        .required('Filter Value is required')
                        .matches(/^(?!^0+$)[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, "Please enter a valid Value"),
                      SortOrder: Yup.string()
                        .matches(/^\d+$/, 'Invalid Sort Order')
                        .required('Sort Order is required')
                        .test('is-number', 'Invalid Sort Order', value => !isNaN(Number(value)))
                        .test('is-min', 'Invalid Sort Order', value => Number(value) >= 1)
                        .test({
                          name: "no-exceed-count",
                          test: function (value) {
                            const filterValues = this.options.context.Filter.Values;
                            if (value > filterValues.length || filterValues.filter(x => Number(x.SortOrder) === Number(value)).length > 1) {
                              return false;
                            }
                            return true;
                          },
                          message: "Invalid Sort Order"
                        })
                        .nullable(),
                      IsDefault: Yup.boolean()
                        .nullable()
                        .required('Is Default is required'),
                      IsEnabled: Yup.boolean()
                        .nullable()
                        .required('Is Enabled is required')
                    })
                  ))
                })
              })}
              onSubmit={async (values) => {
                if (!values.IsInitialLoadWidget && checkedMenuList?.length === 0) {
                  AlertMessage({ type: 'error', msg: 'Widget must be mapped to a page unless it is an Initial Widget' });
                  return;
                }
                // const isValidGroupBy = validateFilterGroupBy();
                // if (!isValidGroupBy) {
                //   return;
                // }
                const isValidPageMapping = validateMappedPageGroups();
                if (!isValidPageMapping) {
                  AlertMessage({ type: 'error', msg: 'Invalid page mapping. Please map to an active page' });
                  return;
                }
                const requestPayload = {
                  ...values,
                  PageMappings: checkedMenuList,
                  API: values.API.map((api) => ({
                    ...api,
                    WidgetSort: api.WidgetSort.map((sortField) => ({
                      ...sortField,
                      WebMethodKey: api.WebMethodKey,
                    })),
                    WidgetFieldFilter: api.WidgetFieldFilter.map((fieldFilter) => ({
                      ...fieldFilter,
                      WebMethodKey: api.WebMethodKey,
                    })),
                    WidgetFieldGrouping: api.WidgetFieldGrouping.map((fieldGrouping) => ({
                      ...fieldGrouping,
                      WebMethodKey: api.WebMethodKey,
                    })),
                  })),
                };
                setIsLoading(true);
                try {
                  const res = await insertUpdateWidgetDetails(requestPayload);
                  if (res.isSuccess) {
                    setIsLoading(false);
                    AlertMessage({ msg: `Widget ${widgetKey ? 'updated' : 'created'} successfully`, type: 'success' });
                    navigate(-1);
                  } else {
                    setIsLoading(false);
                    AlertMessage({ msg: `Failed to ${widgetKey ? 'update' : 'create'} widget`, type: 'error' });

                  }
                } catch (err) {
                  setIsLoading(false);
                  if (err && err.errors) {
                    Object.entries(err.errors).forEach(([key, errMsg]: any) => {
                      AlertMessage({ type: "error", msg: errMsg.errorMessage });
                    });
                  } else {
                    AlertMessage({ type: "error", msg: "An error occurred." });
                  }
                }
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <InputField label="Name *" name="Name" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <SelectField
                            data={categoryOptions}
                            dataTextField={"category"}
                            dataValueField={"widgetCategoryMasterKey"}
                            name="Category"
                            label="Category *"
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <SelectField
                            data={TypeOptions}
                            dataTextField={"type"}
                            dataValueField={"widgetTypeMasterKey"}
                            name="Type"
                            label="Type"
                            onChange={handleTypeChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <InputField label="Tag" name="Tag" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <SelectField
                            data={WidgetTagMatchAllOptions}
                            dataTextField="Label"
                            dataValueField="Value"
                            name="WidgetTagMatchAll"
                            label="Widget Tag Match All"
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <InputField
                            label="Row Count"
                            name="RowNumber"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Number of rows shown in mobile application for tables. Default is 5" classes={{ tooltip: classes.tooltip }}>
                                    <Info style={{ cursor: 'pointer' }} fontSize="small" />
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={shouldCheckInitialLoad}
                                onChange={(event) => handleInitialLoadWidget(event)}
                              />
                            }
                            label="Is Initial Load Widget"
                            labelPlacement="start"
                          />
                        </Grid>
                        {(selectedWidgetType === 'table') && (
                          <>
                            <Grid container spacing={2} sx={{ mt: "1px", ml: "2px" }}>
                              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Divider />
                                <Typography variant="h5" sx={{ mt: "5px" }}>Widget Group Formats</Typography>
                              </Grid>
                              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <SelectField
                                  name={`WidgetGroupFormat[0].FieldType`}
                                  data={FieldTypeOptions}
                                  dataTextField="fieldType"
                                  dataValueField="fieldDataTypeMasterKey"
                                  label={`Group By Field`}
                                  onChange={(event) => {
                                    const selectedFieldKey = event?.fieldDataTypeMasterKey ?? null;
                                    setSelectedFieldType(selectedFieldKey);
                                    formRef?.current?.setFieldValue(`WidgetGroupFormat[0].FieldType`, selectedFieldKey);
                                    formRef?.current?.setFieldValue(`WidgetGroupFormat[0].Format`, null);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <SelectField
                                  name={`WidgetGroupFormat[0].Format`}
                                  data={FieldFormatOptions.filter(
                                    (x) => x.fieldDataTypeMasterKey === selectedFieldType
                                  )}
                                  dataTextField="format"
                                  dataValueField="formatMasterKey"
                                  label={`Group By Format`}
                                />
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                      <Grid container spacing={2} sx={{ mt: "1px" }}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Divider />
                          <Typography variant="h5" sx={{ mt: "5px" }}>Widget Custom Formats</Typography>
                        </Grid>
                        <FieldArray name="WidgetCustomFormat">
                          {({ push: pushCustomFormat, remove: removeCustomFormat }) => (
                            <>
                              {formRef?.current?.values.WidgetCustomFormat?.map((format, formatIdx) => (
                                <Grid container spacing={2} key={formatIdx} sx={{ mt: "3px", ml: '2px' }}>
                                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                                    <SelectField
                                      name={`WidgetCustomFormat.${formatIdx}.DisplayName`}
                                      data={customFormatOptions}
                                      dataTextField={"displayName"}
                                      dataValueField={"displayName"}
                                      label="Field *"
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                                    <SelectField
                                      name={`WidgetCustomFormat.${formatIdx}.FieldType`}
                                      data={FieldTypeOptions}
                                      dataTextField="fieldType"
                                      dataValueField="fieldDataTypeMasterKey"
                                      label="Type *"
                                      onChange={(event) => {
                                        const selectedFieldKey = event?.fieldDataTypeMasterKey ?? null;
                                        if (selectedFieldKey) {
                                          formRef?.current?.setFieldValue(`WidgetCustomFormat.${formatIdx}.FieldType`, selectedFieldKey);
                                        } else {
                                          formRef?.current?.setFieldValue(`WidgetCustomFormat.${formatIdx}.FieldType`, null);
                                        }
                                        formRef?.current?.setFieldValue(`WidgetCustomFormat.${formatIdx}.Format`, null);
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                                    <SelectField
                                      name={`WidgetCustomFormat.${formatIdx}.Format`}
                                      data={FieldFormatOptions.filter((x) => x.fieldDataTypeMasterKey === formRef?.current?.values.WidgetCustomFormat[formatIdx]?.FieldType) || []}
                                      dataTextField="format"
                                      dataValueField="formatMasterKey"
                                      label="Format *"
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                                    <IconButton onClick={() => removeCustomFormat(formatIdx)}>
                                      <Delete />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              ))}
                              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <Button
                                  sx={{
                                    background: "linear-gradient(to bottom, #072f40, #072f40)",
                                    minWidth: 100,
                                    color: "#fff",
                                  }}
                                  onClick={() =>
                                    pushCustomFormat({ DisplayName: '', FieldType: '', Format: '' })
                                  }
                                >
                                  Add Custom Format
                                </Button>
                              </Grid>
                            </>
                          )}
                        </FieldArray>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <APIConfigurationsCard
                    webMethodsList={webMethodsData}
                    setAreDeleteChanges={setAreDeleteChanges}
                    FieldTypeOptions={FieldTypeOptions}
                    FieldFormatOptions={FieldFormatOptions}
                    APIResponseParams={APIResponseParams}
                    selectedWidgetType={selectedWidgetType}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                  <WidgetFilterCard formRef={formRef} setAreDeleteChanges={setAreDeleteChanges} />
                </Grid>
                <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                  <Accordion defaultExpanded style={{ overflowY: "auto", maxHeight: "350px" }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Typography>
                        <h3>Menu and Sub Menu</h3>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <MenuTreeForWidgets
                        isTreeLoading={isTreeLoading}
                        data={menuList}
                        checkedList={checkedMenuList}
                        setCheckedList={setCheckedMenuList}
                        flatMenuList={pageGroupList}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </Formik>
          </Box>
        </Container>
        <Dialog
          open={cancelDialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setCancelDialogOpen(false);
            }
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Your changes are not saved. Are you sure you want to cancel?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                background:
                  "linear-gradient(to bottom, #939ca3, #939ca3)",
                minWidth: 100,
              }}
              variant="contained"
              onClick={() => setCancelDialogOpen(false)}
            >
              No
            </Button>
            <Button
              sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100 }}
              variant="contained"
              onClick={() => navigate("/widgets")}
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default WidgetCreateEditNew;
